import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { availabilityAPI } from '../../services/api';

// Get initial state
const getInitialState = () => {
  return {
    summary: null,
    pausedServices: [],
    scheduledPauses: [],
    availableServices: [],
    isLoading: false,
    error: null,
  };
};

// ==================== GET PAUSED SERVICES ====================
export const getPausedServices = createAsyncThunk(
  'availability/getPausedServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await availabilityAPI.getPausedServices();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to fetch paused services.'
      );
    }
  }
);

// ==================== CREATE PAUSE ====================
export const createPause = createAsyncThunk(
  'availability/createPause',
  async (data, { rejectWithValue }) => {
    try {
      const response = await availabilityAPI.createPause(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to create pause.'
      );
    }
  }
);

// ==================== RESUME SERVICE ====================
export const resumeService = createAsyncThunk(
  'availability/resumeService',
  async (pauseId, { rejectWithValue }) => {
    try {
      const response = await availabilityAPI.resumeService({ PauseId: pauseId });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to resume service.'
      );
    }
  }
);

// ==================== CANCEL SCHEDULED PAUSE ====================
export const cancelScheduledPause = createAsyncThunk(
  'availability/cancelScheduledPause',
  async (pauseId, { rejectWithValue }) => {
    try {
      const response = await availabilityAPI.cancelScheduledPause({ PauseId: pauseId });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
        error.response?.data?.message ||
        'Failed to cancel scheduled pause.'
      );
    }
  }
);

// ==================== AVAILABILITY SLICE ====================
const availabilitySlice = createSlice({
  name: 'availability',
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Optimistic update - remove from paused services list
    removePausedServiceOptimistic: (state, action) => {
      const pauseId = action.payload;
      state.pausedServices = state.pausedServices.filter(p => p.PauseId !== pauseId);
    },
    // Optimistic update - add back to paused services (for revert)
    addPausedServiceOptimistic: (state, action) => {
      const pauseData = action.payload;
      state.pausedServices.push(pauseData);
    },
    // Optimistic update - remove from scheduled pauses list
    removeScheduledPauseOptimistic: (state, action) => {
      const pauseId = action.payload;
      state.scheduledPauses = state.scheduledPauses.filter(p => p.PauseId !== pauseId);
    },
    // Optimistic update - add back to scheduled pauses (for revert)
    addScheduledPauseOptimistic: (state, action) => {
      const pauseData = action.payload;
      state.scheduledPauses.push(pauseData);
    },
  },
  extraReducers: (builder) => {
    // GET PAUSED SERVICES
    builder
      .addCase(getPausedServices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPausedServices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.summary = action.payload.Summary || null;
        state.pausedServices = action.payload.PausedServices?.Pauses || [];
        state.scheduledPauses = action.payload.ScheduledPauses?.Pauses || [];
        state.availableServices = action.payload.AvailableServices || [];
        state.error = null;
      })
      .addCase(getPausedServices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // CREATE PAUSE
    builder
      .addCase(createPause.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPause.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createPause.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // RESUME SERVICE
    builder
      .addCase(resumeService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resumeService.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resumeService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // CANCEL SCHEDULED PAUSE
    builder
      .addCase(cancelScheduledPause.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelScheduledPause.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(cancelScheduledPause.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  removePausedServiceOptimistic,
  addPausedServiceOptimistic,
  removeScheduledPauseOptimistic,
  addScheduledPauseOptimistic
} = availabilitySlice.actions;
export default availabilitySlice.reducer;
